/**
 * The workspace-level "period" the Home dashboard exposes on the hero header
 * (desktop) and as a native strip (mobile). Selecting a period overlays a
 * shared date-range token onto every eligible metric widget at query time, so
 * counters and charts align to the same window and the backend zero-fills
 * missing weeks/months across that window.
 *
 * Tokens are a curated subset of the metrics date-range presets (kept in sync
 * with `DATE_RANGE_PRESETS` and the Rust resolver
 * `metrics_filters::resolve_date_range`). The overlay is never persisted onto a
 * widget's baked filters — it lives in user preferences like a viewer filter.
 */

import { formatCompactDateRange } from "@/lib/weekDates";
import { resolveDateRange } from "@/lib/resolveDateRange";

export const HOME_METRICS_PERIODS = [
  { value: "last_30_days", shortLabel: "30d", label: "Last 30 days" },
  { value: "last_3_months", shortLabel: "3mo", label: "Last 3 months" },
  { value: "last_6_months", shortLabel: "6mo", label: "Last 6 months" },
  { value: "year_to_date", shortLabel: "YTD", label: "Year to date" },
] as const;

export type HomeMetricsPeriod = (typeof HOME_METRICS_PERIODS)[number]["value"];

export interface HomeMetricsPeriodDef {
  /** Metrics date-range token (also a valid `open_date_range` token). */
  value: HomeMetricsPeriod;
  /** Compact label for mobile / segmented control. */
  shortLabel: string;
  /** Full label for tooltips and accessibility. */
  label: string;
}

/** Applied when the user has not chosen a period (matches most chart presets). */
export const DEFAULT_HOME_METRICS_PERIOD: HomeMetricsPeriod = "last_30_days";

/** Time bucket that matches a Home period (weeks for rolling windows, months for YTD). */
export type HomeMetricsPeriodGrain = "week" | "month";

/** Narrow an arbitrary string to a known Home period token. */
export function isHomeMetricsPeriod(value: unknown): value is HomeMetricsPeriod {
  return (
    typeof value === "string" &&
    HOME_METRICS_PERIODS.some((p) => p.value === value)
  );
}

/** Full human label for a period token (falls back to the raw token). */
export function homeMetricsPeriodLabel(period: HomeMetricsPeriod): string {
  return HOME_METRICS_PERIODS.find((p) => p.value === period)?.label ?? period;
}

/** Chart grain implied by the workspace period (months for long windows, else week). */
export function homeMetricsPeriodGrain(
  period: HomeMetricsPeriod,
): HomeMetricsPeriodGrain {
  switch (period) {
    case "year_to_date":
    case "last_3_months":
    case "last_6_months":
      return "month";
    default:
      return "week";
  }
}

/** Compact `Jul 26 – Sep 19` span for the selected Home period. */
export function homeMetricsPeriodRangeLabel(
  period: HomeMetricsPeriod,
  now: Date = new Date(),
): string | null {
  const bounds = resolveDateRange(period, now);
  if (!bounds) return null;
  return formatCompactDateRange(bounds.from, bounds.to);
}
